import { useState } from 'react';
import { useModals } from '@customerio/pluma-components/react';
import type { Relationship } from '@customerio/hydra-ui-api';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import type { ObjectType } from '@/services/db/object-type/entity';
import { RelationshipColumnsModal } from './relationship-columns-modal';
import { relationshipKey } from './relationship-customer';
import { RelationshipPreviewDrawer } from './relationship-preview-drawer';
import type { RelationshipRowModel } from './relationship-row';
import { useRelationshipColumns } from './use-relationship-columns';

type UseRelationshipExtrasOptions = {
  workspaceId: number;
  cioObjectId: string;
  objectType: ObjectType;
  rows: readonly RelationshipRowModel[];
  canEdit: boolean;
};

/**
 * What the relationships table offers beyond listing and deleting: the
 * displayed attribute columns and their picker, and the preview drawer a
 * row's attribute cells open.
 */
export function useRelationshipExtras({
  workspaceId,
  cioObjectId,
  objectType,
  rows,
  canEdit,
}: UseRelationshipExtrasOptions) {
  const modals = useModals();
  const identifiers = useEnvironmentOptional(workspaceId).data?.environment?.identifiers;
  const columns = useRelationshipColumns(workspaceId, objectType);
  // The row as it was when its preview opened; the drawer keeps its own copy
  // of the attributes from there. Its overlay covers the table, so no other
  // row, page or delete control is reachable while it is open.
  const [previewRow, setPreviewRow] = useState<RelationshipRowModel | null>(null);

  const openPreview = (relationship: Relationship) => {
    const key = relationshipKey(relationship);
    setPreviewRow(rows.find((row) => row.key === key) ?? null);
  };

  const editColumns = () => {
    modals.open(
      RelationshipColumnsModal,
      { workspaceId, objectTypeId: objectType.id, selected: columns },
      { shouldCloseOnUnmount: true, withImmediateCallbacks: true },
    );
  };

  const drawer =
    previewRow == null ? null : (
      <RelationshipPreviewDrawer
        key={previewRow.key}
        workspaceId={workspaceId}
        cioObjectId={cioObjectId}
        objectTypeId={objectType.id}
        relationship={previewRow.relationship}
        customer={previewRow.customer}
        personName={previewRow.personName}
        personHref={previewRow.personHref}
        identifiers={identifiers}
        knownAttributes={objectType.sent_rel_attributes ?? []}
        canEdit={canEdit}
        onClose={() => setPreviewRow(null)}
      />
    );

  return { columns, openPreview, editColumns, drawer };
}
